import { type ComponentPropsWithoutRef, forwardRef, memo, useCallback } from 'react';

import { Hamburger } from '@air/next-icons';

import { WorkspaceLogo } from '~/components/v2/components/shared/WorkspaceLogo';
import { WorkspaceSidebarToken } from '~/components/v2/components/workspace/workspace-sidebar/getWorkspaceSidebarEventToken';
import { useWorkspaceNavPushMode } from '~/components/v2/hooks/useWorkspaceNavPushMode';
import { useWorkspaceNotificationsStore } from '~/components/v2/store/features/workspace/useWorkspaceNotificationsStore';
import { useWorkspaceSidebarPushStore } from '~/components/v2/store/features/workspace/useWorkspaceSidebarPushStore';
import { useWorkspaceSidebarStore } from '~/components/v2/store/features/workspace/useWorkspaceSidebarStore';
import { CURRENT_WORKSPACE_BUTTON } from '~/constants/testIDs';

const LOGO_DIV = (
  <div className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-8 border border-grey-5 bg-grey-5">
    <WorkspaceLogo />
  </div>
);

const HamburgerButton = memo(
  forwardRef<HTMLButtonElement, ComponentPropsWithoutRef<'button'>>((props, ref) => (
    <button
      aria-label="Toggle sidebar"
      className="flex size-8 shrink-0 items-center justify-center rounded-8 text-grey-10 outline-none hover:bg-grey-5 focus-visible:ring-2 focus-visible:ring-blue-9"
      data-testid={CURRENT_WORKSPACE_BUTTON}
      ref={ref}
      type="button"
      {...props}
    >
      <Hamburger className="size-4" />
    </button>
  )),
);

HamburgerButton.displayName = 'HamburgerButton';

export const WorkspaceHeaderInformationLogo = memo(() => {
  const open = useWorkspaceSidebarStore((store) => store.open);
  const toggleOpen = useWorkspaceSidebarStore((store) => store.toggleOpen);
  const setNotificationsOpen = useWorkspaceNotificationsStore((store) => store.setOpen);
  const { isWorkspaceNavPushMode } = useWorkspaceNavPushMode();
  const openPush = useWorkspaceSidebarPushStore((s) => s.openPush);
  const suppressHoverOpen = useWorkspaceSidebarPushStore((s) => s.suppressHoverOpen);
  const unsuppressHoverOpen = useWorkspaceSidebarPushStore((s) => s.unsuppressHoverOpen);

  const handleToggle = useCallback(() => {
    if (isWorkspaceNavPushMode) {
      return;
    }

    if (open) {
      suppressHoverOpen(WorkspaceSidebarToken.Hamburger);
    }
    toggleOpen();
    setNotificationsOpen(false);
  }, [open, toggleOpen, setNotificationsOpen, isWorkspaceNavPushMode, suppressHoverOpen]);

  const handleHoverOpen = useCallback(() => {
    openPush();
  }, [openPush]);

  const handleHamburgerLeave = useCallback(() => {
    unsuppressHoverOpen(WorkspaceSidebarToken.Hamburger);
  }, [unsuppressHoverOpen]);

  if (!open) {
    return (
      <>
        <HamburgerButton
          onClick={handleToggle}
          onPointerEnter={handleHoverOpen}
          onPointerLeave={handleHamburgerLeave}
        />
        {LOGO_DIV}
      </>
    );
  }

  return (
    <button
      aria-label="Toggle sidebar"
      className="group relative size-8 shrink-0 overflow-hidden rounded-8 border border-grey-5 bg-grey-5 outline-none focus-visible:ring-2 focus-visible:ring-blue-9"
      data-testid={CURRENT_WORKSPACE_BUTTON}
      onClick={handleToggle}
      type="button"
    >
      <WorkspaceLogo className="opacity-100 transition-opacity group-hover:opacity-0 group-focus-visible:opacity-0" />
      <div className="absolute inset-0 flex items-center justify-center bg-grey-5 text-grey-10 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
        <Hamburger className="size-4" />
      </div>
    </button>
  );
});

WorkspaceHeaderInformationLogo.displayName = 'WorkspaceHeaderInformationLogo';
